if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      productSwapUtilityInitialized = false;
      _variantChangeTimeout = null;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        if (!this.productSwapUtilityInitialized) this.initializeProductSwapUtility();
        // eslint-disable-next-line no-undef
        this.onVariantChangeUnsubscriber = subscribe(
          // eslint-disable-next-line no-undef
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();

        this.updateViewDetailsLink(this.getSelectedVariant(this)?.id);
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        const mediaGallery = this.getMediaGallery();
        if (mediaGallery?.hasAttribute('data-mto-variant-images') && !mediaGallery._mtoThumbnailHandler) {
          this._attachMtoThumbnailHandler(mediaGallery);
        }
      }

      getMediaGallery(root = this) {
        const galleries = Array.from(root.querySelectorAll('media-gallery'));
        if (!this.closest('quick-add-modal')) return galleries[0];

        return galleries.find((gallery) => gallery.id.startsWith('MediaGallery-QuickAdd-')) || galleries[0];
      }

      refreshQuickViewMedia(sourceGallery, destinationGallery, variantFeaturedMediaId) {
        const sourceList = sourceGallery?.querySelector('ul[id^="Slider-Gallery"]');
        const destinationList = destinationGallery?.querySelector('ul[id^="Slider-Gallery"]');
        if (!sourceList || !destinationList) return false;

        sourceList.innerHTML = destinationList.innerHTML;
        sourceGallery.refresh?.();

        const viewer = sourceGallery.elements?.viewer;
        if (viewer?.slider) {
          viewer.slider.scrollLeft = 0;
          viewer.resetPages?.();
        }

        const nativeMediaId = variantFeaturedMediaId ? `${this.dataset.section}-${variantFeaturedMediaId}` : null;
        const renderedActiveMedia = sourceList.querySelector('li.is-active[data-media-id]');
        const activeMediaId =
          nativeMediaId && sourceList.querySelector(`[data-media-id="${nativeMediaId}"]`)
            ? nativeMediaId
            : renderedActiveMedia?.dataset.mediaId;
        if (activeMediaId) sourceGallery.setActiveMedia?.(activeMediaId, true);

        if (sourceGallery._mtoThumbnailHandler) {
          sourceGallery.removeEventListener('click', sourceGallery._mtoThumbnailHandler);
          delete sourceGallery._mtoThumbnailHandler;
        }
        if (sourceGallery.hasAttribute('data-mto-variant-images')) {
          this._attachMtoThumbnailHandler(sourceGallery);
        }
        return true;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      _attachMtoThumbnailHandler(mediaGallery) {
        mediaGallery._mtoThumbnailHandler = (event) => {
          const thumbnailItem = event.target.closest('[data-target]');
          if (!thumbnailItem) return;

          const targetId = thumbnailItem.dataset.target;
          const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
          if (!viewer) return;

          viewer.querySelectorAll('[data-media-id]').forEach((slide) => {
            slide.classList.remove('is-active');
          });

          const activeSlide = viewer.querySelector(`[data-media-id="${targetId}"]`);
          if (activeSlide) {
            activeSlide.classList.add('is-active');
          }

          const thumbnailList = mediaGallery.querySelector('[id^="GalleryThumbnails"]');
          if (thumbnailList) {
            thumbnailList.querySelectorAll('button').forEach((btn) => btn.removeAttribute('aria-current'));
            const activeThumbnailBtn = thumbnailItem.querySelector('button');
            if (activeThumbnailBtn) activeThumbnailBtn.setAttribute('aria-current', 'true');
          }
        };
        mediaGallery.addEventListener('click', mediaGallery._mtoThumbnailHandler);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          // eslint-disable-next-line no-undef
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        clearTimeout(this._variantChangeTimeout);
        this._variantChangeTimeout = null;
        this.abortController?.abort();
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.onVariantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber = undefined;
      }

      initializeProductSwapUtility() {
        if (this.productSwapUtilityInitialized) return;
        this.productSwapUtilityInitialized = true;
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel')),
        );
        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // Detect iOS Safari - debounce only on iOS to prevent race conditions
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isIOSSafari = isIOS && isSafari;

        const executeRender = () => {
          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
            targetId: target.id,
            callback: shouldSwapProduct
              ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
              : this.handleUpdateProductInfo(productUrl),
          });
        };

        if (isIOSSafari) {
          // Debounce on iOS Safari to prevent race conditions
          clearTimeout(this._variantChangeTimeout);
          this._variantChangeTimeout = setTimeout(() => {
            executeRender();
            this._variantChangeTimeout = null;
          }, 100);
        } else {
          // Execute immediately on other browsers
          executeRender();
        }
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            // eslint-disable-next-line no-undef
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            // eslint-disable-next-line no-undef
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        // Store the current request URL to detect if it's still the latest request when it completes
        const currentRequestUrl = requestUrl;
        this._latestRequestUrl = currentRequestUrl;

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.text();
          })
          .then((responseText) => {
            // Only process if this is still the latest request (prevents race conditions)
            if (this._latestRequestUrl !== currentRequestUrl) {
              console.log('Skipping outdated response');
              return;
            }

            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);

            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              // Silently ignore abort errors - they're expected when user changes selection quickly
              return;
            } else {
              console.error('Error fetching product info:', error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        // eslint-disable-next-line no-extra-boolean-cast
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          // eslint-disable-next-line no-undef
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const urgencytargetNode = document.getElementById('urgency-engine-snippet-container');
          if (urgencytargetNode) {
            this.updateUrgencyEngineSnippet(variant?.id);
          }
          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateViewDetailsLink(variant?.id);

          if (!variant) {
            // eslint-disable-next-line no-undef
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant: null,
              },
            });
            this.setUnavailable();
            return;
          }

          // Check if this is an MTO product with variant-specific images
          const mediaGallery = this.getMediaGallery();
          const isMtoWithVariantImages = mediaGallery?.hasAttribute('data-mto-variant-images');

          if (isMtoWithVariantImages) {
            // For MTO products, merge the rendered variant media into the existing gallery
            this.updateMediaGalleryForMto(html, variant?.featured_media?.id);
          } else {
            // For non-MTO products, use the original updateMedia logic
            this.updateMedia(html, variant?.featured_media?.id);
          }

          const updateSourceFromDestination = (id, shouldHide = () => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('ShippingCallouts');
          updateSourceFromDestination('PerksExclusiveAddButton');

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut,
          );
          // eslint-disable-next-line no-undef
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateUrgencyEngineSnippet(variantId) {
        const sectionId = 'urgency-evaluation-engine'; // Filename of your section
        const url = `${window.location.pathname}?variant=${variantId}&section_id=${sectionId}`;

        // Guard against out-of-order responses when the shopper switches
        // variants quickly - only the latest request may apply its markup.
        const requestToken = (this._urgencyRequestToken || 0) + 1;
        this._urgencyRequestToken = requestToken;

        fetch(url)
          .then((response) => response.text())
          .then((html) => {
            if (this._urgencyRequestToken !== requestToken) return;
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newContent = doc.getElementById('shopify-section-urgency-evaluation-engine');
            const oldContent = document.getElementById('urgency-engine-snippet-container');

            if (oldContent && newContent) {
              // Triggers the MutationObserver in urgency-engine.js, which
              // re-runs UrgencyInit.evaluate() against this fresh markup.
              oldContent.innerHTML = newContent.innerHTML;
            }
          })
          .catch((error) => console.error('Error fetching section:', error));
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateViewDetailsLink(variantId) {
        const viewDetailsLink = this.querySelector('.product__view-details');
        if (!viewDetailsLink) return;

        if (!viewDetailsLink.dataset.baseUrl) {
          viewDetailsLink.dataset.baseUrl = viewDetailsLink.getAttribute('href').split('?')[0];
        }

        const baseUrl = viewDetailsLink.dataset.baseUrl;
        viewDetailsLink.href = variantId ? `${baseUrl}?variant=${variantId}` : baseUrl;
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMediaGalleryForMto(html, variantFeaturedMediaId) {
        const mediaGallerySource = this.getMediaGallery();
        const mediaGalleryDestination = this.getMediaGallery(html);

        if (!mediaGallerySource || !mediaGalleryDestination) return;

        if (this.closest('quick-add-modal')) {
          this.refreshQuickViewMedia(mediaGallerySource, mediaGalleryDestination, variantFeaturedMediaId);
          return;
        }

        const mergeMediaItems = (sourceList, destinationList) => {
          if (!sourceList || !destinationList) return;

          const existingMediaIds = new Set(
            [...sourceList.querySelectorAll('li[data-media-id]')].map((item) => item.dataset.mediaId),
          );
          destinationList.querySelectorAll('li[data-media-id]').forEach((item) => {
            if (existingMediaIds.has(item.dataset.mediaId)) return;
            sourceList.append(item.cloneNode(true));
            existingMediaIds.add(item.dataset.mediaId);
          });
        };

        mergeMediaItems(
          mediaGallerySource.querySelector('ul[id^="Slider-Gallery"]'),
          mediaGalleryDestination.querySelector('ul[id^="Slider-Gallery"]'),
        );
        mergeMediaItems(
          mediaGallerySource.querySelector('ul[id^="Slider-Thumbnails"]'),
          mediaGalleryDestination.querySelector('ul[id^="Slider-Thumbnails"]'),
        );

        const galleryList = mediaGallerySource.querySelector('ul[id^="Slider-Gallery"]');
        const mediaCount = galleryList?.querySelectorAll('li[data-media-id]').length ?? 0;
        mediaGallerySource.querySelectorAll('.slider-counter--total').forEach((counter) => {
          counter.textContent = mediaCount;
        });
        mediaGallerySource.refresh?.();

        const renderedActiveMedia = mediaGalleryDestination.querySelector(
          'ul[id^="Slider-Gallery"] li.is-active[data-media-id]',
        );
        const activeMediaId = variantFeaturedMediaId
          ? `${this.dataset.section}-${variantFeaturedMediaId}`
          : renderedActiveMedia?.dataset.mediaId;
        if (activeMediaId) mediaGallerySource.setActiveMedia?.(activeMediaId, true);

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isIOSSafari = isIOS && isSafari;

        window.requestAnimationFrame(() => {
          if (mediaGallerySource._mtoThumbnailHandler) {
            mediaGallerySource.removeEventListener('click', mediaGallerySource._mtoThumbnailHandler);
            delete mediaGallerySource._mtoThumbnailHandler;
          }
          this._attachMtoThumbnailHandler(mediaGallerySource);

          if (isIOSSafari) {
            void mediaGallerySource.offsetHeight;
            mediaGallerySource.querySelectorAll('img').forEach((img) => {
              if (!img.src) return;
              const originalSrc = img.src;
              img.src = '';
              window.requestAnimationFrame(() => {
                img.src = originalSrc;
              });
            });
          }
        });

        const modalContent = this.productModal?.querySelector('.product-media-modal__content');
        const newModalContent = html.querySelector('product-modal .product-media-modal__content');
        if (modalContent && newModalContent) {
          const existingModalMediaIds = new Set(
            [...modalContent.children]
              .map((item) => item.querySelector('[data-media-id]')?.dataset.mediaId)
              .filter(Boolean),
          );
          [...newModalContent.children].forEach((item) => {
            const mediaId = item.querySelector('[data-media-id]')?.dataset.mediaId;
            if (mediaId && !existingModalMediaIds.has(mediaId)) {
              modalContent.append(item.cloneNode(true));
              existingModalMediaIds.add(mediaId);
            }
          });
        }
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (this.closest('quick-add-modal')) {
          const mediaGallerySource = this.getMediaGallery();
          const mediaGalleryDestination = this.getMediaGallery(html);
          if (mediaGallerySource && mediaGalleryDestination) {
            this.refreshQuickViewMedia(mediaGallerySource, mediaGalleryDestination, variantFeaturedMediaId);
          }
          return;
        }

        const mediaGallerySource = Array.from(this.querySelectorAll('media-gallery ul')).find((element) => {
          // Use fallback for Safari on iOS where checkVisibility() is not supported
          if (typeof element.checkVisibility === 'function') {
            return element.checkVisibility();
          }
          // Fallback: check if element is visible using offsetParent and display/visibility styles
          if (!element.offsetParent && element.offsetWidth === 0 && element.offsetHeight === 0) {
            return false;
          }
          const style = window.getComputedStyle(element);
          return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
        });
        if (!mediaGallerySource) return;

        const mediaGalleryDestination = Array.from(html.querySelectorAll(`media-gallery ul`)).find(
          (element) => element.id.split('-').slice(0, 3).join() === mediaGallerySource.id.split('-').slice(0, 3).join(),
        );
        if (!mediaGalleryDestination) return;

        const renderedActiveMedia = mediaGalleryDestination.querySelector('li.is-active[data-media-id]');

        const refreshSourceData = () => {
          // eslint-disable-next-line no-undef
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // Set the selected media active in the visible gallery. Some variant
        // payloads omit native featured_media, so use the rendered active slide.
        const nativeMediaId = variantFeaturedMediaId ? `${this.dataset.section}-${variantFeaturedMediaId}` : null;
        const activeMediaId =
          nativeMediaId && mediaGallerySource.querySelector(`[data-media-id="${nativeMediaId}"]`)
            ? nativeMediaId
            : renderedActiveMedia?.dataset.mediaId;
        if (activeMediaId) this.getMediaGallery()?.setActiveMedia?.(activeMediaId, true);

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;
        // eslint-disable-next-line no-undef
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        // eslint-disable-next-line no-undef
        const relatedProductsSectionId = SectionId.getIdForSection(
          // eslint-disable-next-line no-undef
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        // eslint-disable-next-line no-undef
        const quickOrderListSectionId = SectionId.getIdForSection(
          // eslint-disable-next-line no-undef
          SectionId.parseId(this.sectionId),
          'quick_order_list',
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}
